// This file configures the initialization of Sentry for edge features (middleware, edge routes, and so on).
// The config you add here will be used whenever one of the edge features is loaded.
// Note that this config is unrelated to the Vercel Edge Runtime and is also required when running locally.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import { BrowserOptions, ErrorEvent, EventHint } from '@sentry/nextjs'

import { isPrd } from '@shared/util/env'

const dsn =
    process.env.SENTRY_DSN ||
    'https://8757ab63d5a842d69add259495c1327a@o177913.ingest.us.sentry.io/1385134'

function beforeSend(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
    const error = hint.originalException
    const message = error instanceof Error ? error.message : String(error || '')

    // Filter transient 'aborted' errors (Sentry issue WEB-AR4)
    if (/\baborted\b/i.test(message)) {
        return null
    }

    // Filter expected card payment errors (Sentry issue WEB-ASF)
    // These are user-facing errors (card declined, invalid card, etc.) not system failures
    const cardErrorCodes = [
        'CARD_DECLINED',
        'CARD_NUMBER_INVALID',
        'INVALID_CARD_TYPE',
        'UNSUPPORTED_CARD',
        'CVV_REQUIRED',
        'CVV_NOT_MATCHED',
        'AVS_NOT_MATCHED',
    ]
    if (cardErrorCodes.some((code) => message.includes(code))) {
        return null
    }

    // Filter XMLHttpRequestProgressEvent from mobile network instability (Sentry issue WEB-ANQ)
    // These occur when XHR calls from third-party SDKs (Braze, Mixpanel, AppsFlyer) get
    // aborted due to network interruptions on Mobile Safari/iOS
    if (message.includes('XMLHttpRequestProgressEvent')) {
        return null
    }

    // Filter webkit messageHandlers errors from third-party scripts in iOS webviews (Sentry issue WEB-AWP)
    // These occur when external scripts (GTM, analytics) access window.webkit.messageHandlers
    // without null checking in Facebook's in-app browser on iOS
    if (message.includes('webkit.messageHandlers')) {
        return null
    }

    // Filter Facebook in-app browser autofill bridge errors (Sentry issue WEB-AXN)
    // These occur when Facebook's injected autofill JavaScript tries to access
    // .value on form elements that have been conditionally removed from the DOM
    const frames = event.exception?.values?.[0]?.stacktrace?.frames || []
    const isFacebookAutofillError = frames.some(
        (frame) =>
            frame.function?.includes('setContactAutofillValuesFromBridge') ||
            frame.function?.includes('AutofillValuesFromBridge'),
    )
    if (isFacebookAutofillError) {
        return null
    }

    // Filter native bridge errors from third-party SDKs in mobile WebViews (Sentry issue WEB-ANX)
    // These occur when AppsFlyer Smart Script or Branch.io SDK's native bridge
    // fails to find expected native handlers in WebView contexts
    if (message.includes('Object Not Found Matching Id:')) {
        return null
    }

    // Filter Sentry Proxy/AppsFlyer fingerprinting conflict (Sentry issue WEB-B4G)
    // Sentry's browserApiErrorsIntegration wraps navigator with a Proxy, but
    // navigator.javaEnabled is read-only/non-configurable, causing a Proxy invariant
    // violation when AppsFlyer SDK's fingerprinting accesses it
    if (message.includes("'get' on proxy") && message.includes('javaEnabled')) {
        return null
    }

    // Filter AppsFlyer PBA SDK initialization errors (Sentry issues WEB-B4B, WEB-B4C)
    // The SDK throws when window.AF.plugins is undefined during async script execution.
    // Chrome: "Cannot set properties of undefined (setting 'PBA')"
    // Firefox: "can't access property "PBA", window.AF.plugins is undefined"
    if (message.includes("setting 'PBA'") || message.includes('AF.plugins')) {
        return null
    }

    // Filter browser extension errors from injected inpage.js scripts (Sentry issue WEB-B6J)
    // Crypto wallet extensions (MetaMask, Phantom, etc.) inject inpage.js into all pages;
    // errors from these scripts are not Step code
    const isExtensionInpageError =
        frames.length > 0 &&
        frames.every((frame) => frame.filename?.includes('inpage.js'))
    if (isExtensionInpageError) {
        return null
    }

    // Filter Cookiebot dialog initialization errors in restricted browsers (Sentry issue WEB-B8S)
    // Cookiebot's cc.js calls document.createEvent inside CookieControl.Dialog.init,
    // which fails in Facebook's iOS in-app browser WebView sandbox
    const isCookieControlError = frames.some((frame) =>
        frame.function?.includes('CookieControl'),
    )
    if (isCookieControlError) {
        return null
    }

    // Filter unsupported HTTP method errors from security scanners (Sentry issue WEB-ATW)
    // Bots send TRACE/TRACK requests that Next.js rejects — 0 users impacted
    if (message.includes('HTTP method is unsupported')) {
        return null
    }

    // Filter removeChild DOM errors caused by browser extensions/third-party scripts (Sentry issue WEB-APN)
    // Extensions (ad blockers, Grammarly, translation tools, crypto wallets) modify the DOM outside
    // React's control; when React reconciles, it fails to find the moved/removed nodes
    if (
        message.includes('The node to be removed is not a child of this node')
    ) {
        return null
    }

    // Filter InvalidAccessError from Sentry's postMessage wrapping in Facebook iOS WebView (Sentry issue WEB-ATZ)
    // Sentry's BrowserApiErrors integration wraps postMessage arguments via wrap(), which fails
    // in Facebook's in-app browser sandbox with DOMException code 15. No functional impact.
    if (
        message.includes('InvalidAccessError') &&
        frames.some((frame) => frame.function === 'postMessage')
    ) {
        return null
    }

    return event
}

export const config: BrowserOptions = {
    dsn,
    enabled: isPrd(),
    environment: process.env.SENTRY_ENVIRONMENT,
    beforeSend,
}
